import { useState, useMemo, memo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Check, AlertCircle, Eye, EyeOff, Loader2, CheckCircle, MessageCircle, Mail, Gift, User, Sparkles, Brain, Monitor, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { InAppBrowserWarning } from '@/components/InAppBrowserWarning';
import { SUPPORT } from '@/lib/constants';
import { suggestEmailFix } from '@/lib/emailTypo';
import { trackEvent, trackCtaClick, ANALYTICS_EVENTS } from '@/lib/analytics';
import { friendlyError } from '@/lib/friendlyError';
import { isAccountExistsError } from '@/lib/accountExists';
import { supabase } from '@/integrations/supabase/client';
import { trackAuthStep } from '@/lib/authFunnel';

// ============================================
// PREMIUM LIGHT INPUT
// ============================================
const PremiumInput = memo(function PremiumInput({
  type,
  value,
  onChange,
  placeholder,
  label,
  disabled,
  error,
  success,
}: {
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  disabled?: boolean;
  error?: string;
  success?: boolean;
  autoFocus?: boolean;
}) {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  // id/name estaveis entre deploys, que e o que o gerenciador de senha usa para
  // lembrar a conta. Antes eram ausentes: so o autoComplete="email" solto, que
  // oferece emails salvos mas nao casa o par email+senha.
  const fieldId = type === 'email' ? 'email' : type === 'password' ? 'new-password' : 'name';

  return (
    <div className="space-y-2">
      {/* htmlFor ligado ao id do campo (17/09/2026). A label existia solta no
          DOM: leitor de tela nao associava, e o gerenciador de senha tinha uma
          pista a menos para casar o par email+senha. */}
      <label
        htmlFor={fieldId}
        className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider"
      >
        {label}
      </label>

      <div className="relative group">
        <input
          id={fieldId}
          name={type === 'password' ? 'password' : fieldId}
          type={inputType}
          inputMode={type === 'email' ? 'email' : undefined}
          enterKeyHint={type === 'password' ? 'go' : 'next'}
          required
          aria-label={label}
          autoComplete={
            type === 'email' ? 'username' : type === 'password' ? 'new-password' : 'name'
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            w-full h-14 px-4 pr-12 rounded-2xl
            bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-foreground text-base
            placeholder:text-gray-500 dark:placeholder:text-gray-500
            outline-none
            transition-all duration-300
            disabled:opacity-40 disabled:cursor-not-allowed
            ${error
              ? 'ring-2 ring-red-500/60 bg-red-50 dark:bg-red-950/30'
              : success
                ? 'ring-2 ring-orange-500/40 bg-orange-50 dark:bg-orange-950/20'
                : 'ring-1 ring-gray-200 dark:ring-white/10 focus:ring-2 focus:ring-orange-500/50 focus:bg-white dark:focus:bg-white/15'
            }
          `}
        />

        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isPassword && value.length > 0 && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="p-2 -mr-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          )}

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-red-700 dark:text-red-500"
              >
                <AlertCircle className="w-5 h-5" />
              </motion.div>
            )}
            {success && !error && (
              <motion.div
                key="success"
                initial={{ scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-accent"
              >
                <Check className="w-5 h-5" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-red-700 dark:text-red-500 text-[15px]"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
});

// ============================================
// PASSWORD STRENGTH
// ============================================
const PasswordStrength = memo(function PasswordStrength({ password }: { password: string }) {
  const strength = useMemo(() => {
    if (password.length === 0) return { score: 0, label: '', color: '' };
    if (password.length < 6) return { score: 1, label: 'Too short', color: 'bg-red-400' };

    let score = 2;
    if (password.length >= 8) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 3) return { score: 2, label: 'Fair', color: 'bg-yellow-400' };
    if (score <= 4) return { score: 3, label: 'Good', color: 'bg-orange-400' };
    return { score: 4, label: 'Strong', color: 'bg-emerald-400' };
  }, [password]);

  if (password.length === 0) return null;

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="mt-2"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`
                h-1.5 flex-1 rounded-full transition-colors
                ${level <= strength.score ? strength.color : 'bg-gray-200 dark:bg-white/10'}
              `}
            />
          ))}
        </div>
        <span className="text-[15px] font-medium text-gray-600 dark:text-gray-400">{strength.label}</span>
      </div>
    </motion.div>
  );
});


// ============================================
// WHAT'S INCLUDED
// ============================================
const WhatsIncluded = memo(function WhatsIncluded() {
  const items = [
    { icon: Sparkles, text: '200+ magic phrases for any situation' },
    { icon: MessageCircle, text: 'Sarah, your personal parenting coach' },
    { icon: Brain, text: 'Personalized for your child\'s age & behavior' },
    { icon: Monitor, text: 'Use it before bedtime tonight' },
  ];

  return (
    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-white/5 ring-1 ring-gray-200 dark:ring-white/10">
      <p className="text-xs font-semibold text-orange-600 uppercase tracking-widest mb-3">
        Your purchase includes
      </p>
      <div className="space-y-2.5">
        {items.map((item, i) => (
          <motion.div
            key={i}
            initial={{ x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + i * 0.08 }}
            className="flex items-center gap-3"
          >
            <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <item.icon className="w-3.5 h-3.5 text-orange-600" />
            </div>
            <span className="text-gray-700 dark:text-gray-300 text-[15px]">{item.text}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
});

// FIX 13: Proper email validation regex (module-level, not recreated on every render)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ============================================
// MAIN COMPONENT
// ============================================
export default function Welcome() {
  const navigate = useNavigate();
  const { signUp, user } = useAuth();

  const [mode, setMode] = useState<'self' | 'gift'>('self');

  // ============================================================
  // 16/09/2026 — o email do comprador chega na URL? Pré-preenche se chegar.
  //
  // O problema medido: ~16% de toda compra Hotmart nunca vira conta (117 de 731
  // nos últimos 30 dias, e a mesma faixa nas coortes anteriores). O email foi
  // aceito pelo Resend em 721 das 731, então não é falha de envio. Aqui a pessoa
  // tem que DIGITAR de novo um email que precisa bater exatamente com o da
  // compra, senão leva "This email doesn't have a purchase".
  //
  // Duas entradas caem nesta página: a de obrigado da Hotmart e o link do nosso
  // próprio email de boas-vindas. Cobrimos as duas: `e` é o nosso, o resto são os
  // nomes prováveis do lado da Hotmart. Se nenhum chegar, o campo fica vazio como
  // sempre foi e nada piora.
  // ============================================================
  const landingParams = useMemo(() => {
    if (typeof window === 'undefined') return { keys: '', prefill: '' };
    const sp = new URLSearchParams(window.location.search);
    const keys = [...sp.keys()].sort().join(',').slice(0, 180);
    const candidate = sp.get('e') || sp.get('email') || sp.get('buyer_email') || sp.get('off_email') || '';
    return { keys, prefill: EMAIL_REGEX.test(candidate) ? candidate : '' };
  }, []);

  // Funil antes da conta (23/09/2026): tela vista, começou a digitar, enviou, e o
  // desfecho. Nenhum valor digitado sai daqui. Ver src/lib/authFunnel.ts.
  const formStarted = useRef(false);
  const markStarted = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackAuthStep('welcome_hp', 'form_started');
  };
  useEffect(() => {
    trackAuthStep('welcome_hp', 'shown', { detail: landingParams.prefill ? 'prefilled' : 'empty' });
  }, [landingParams.prefill]);

  // Self mode states
  const [email, setEmail] = useState(landingParams.prefill);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [accountExists, setAccountExists] = useState(false);

  const emailError = useMemo(() => {
    if (!email) return undefined;
    if (!EMAIL_REGEX.test(email)) return 'Enter a valid email';
    return undefined;
  }, [email]);

  const emailValid = email.length > 0 && !emailError;
  const passwordValid = password.length >= 6;
  const formValid = emailValid && passwordValid;

  // Same "did you mean" card as AuthForm. This page is where most buyers create
  // the account, and it had no suggestion at all: the reconcile of 13/09/2026
  // found 12 typo-domain accounts in 30 days (sncglobal.net, gmsil.com,
  // comcast.netj, yahoo.come), each one a paying customer on an empty screen.
  // Suggests, never blocks.
  const emailSuggestion = useMemo(
    () => (emailValid ? suggestEmailFix(email) : null),
    [email, emailValid],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || loading) return;

    setLoading(true);
    setFormError(null);
    trackAuthStep('welcome_hp', 'submit');

    try {
      const { error } = await signUp(email, password, true);

      if (error) {
        if (error.code === 'EMAIL_NOT_APPROVED' ||
          error.message?.includes('EMAIL_NOT_APPROVED') ||
          error.message?.includes('not approved') ||
          error.message?.includes('Purchase required')) {
          trackAuthStep('welcome_hp', 'email_not_approved', { detail: emailSuggestion ? 'typo_suspected' : undefined });
          setFormError('This email doesn\'t have a purchase. Please use the same email you used when buying.');
          return;
        }

        // Duplicate-email detection. Supabase/GoTrue returns this in several shapes
        // depending on version, so we match on code and a loose regex over the message
        // text. NOT on status 422 alone: GoTrue also uses it for invalid email and weak
        // password (23/09, src/lib/accountExists.ts). Missing any of these sends the user to the red fallback
        // banner with raw Supabase text instead of the friendly "Sign In Now" banner,
        // which is what caused the "email address has been used" confusion in prod.
        const errCode = (error as { code?: string }).code;
        const errStatus = (error as { status?: number }).status;
        const errMsg = error.message || '';
        const alreadyExists = isAccountExistsError({ code: errCode, status: errStatus, message: errMsg });
        if (alreadyExists) {
          trackAuthStep('welcome_hp', 'account_exists');
          setAccountExists(true);
          setFormError(null);
          return;
        }

        trackAuthStep('welcome_hp', /network|fetch|connection|internet/i.test(errMsg) ? 'network_error' : 'other_error', {
          detail: errCode || (errStatus ? `http_${errStatus}` : undefined),
        });
        setFormError(friendlyError(error, 'Something went wrong. Please try again.'));
        return;
      }

      // Liga o funil à conta recém-criada, sem esperar por isso.
      void supabase.auth.getSession().then(({ data }) =>
        trackAuthStep('welcome_hp', 'signup_ok', { userId: data.session?.user?.id }),
      );

      // Só agora existe sessão, então só agora a RLS de insert-own aceita o evento.
      // Grava apenas os NOMES dos parâmetros que vieram na URL: é o que responde
      // "a Hotmart manda o email do comprador?" sem guardar dado de ninguém.
      void trackEvent(ANALYTICS_EVENTS.LANDING_PARAMS, {
        path: '/welcome-hp',
        metadata: { qs_keys: landingParams.keys || '(nenhum)', prefilled: landingParams.prefill.length > 0 },
      });

      toast.success('Account created!', {
        description: 'Welcome to The Code Of Obedience!',
      });
      navigate('/');
    } catch (err) {
      trackAuthStep('welcome_hp', 'exception');
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden bg-[#FAFAF8] dark:bg-background"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Atmospheric background */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Warm gradient orb top-left */}
        <div
          className="absolute -top-[200px] -left-[150px] w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,107,0,0.06) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
        {/* Cool accent bottom-right */}
        <div
          className="absolute -bottom-[200px] -right-[150px] w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col px-5 py-8 max-w-md mx-auto w-full">

        {/*
          Quem JÁ está logado e cai aqui (16/09/2026).

          Esta página nunca olhou o `user`, então quem já tinha conta ficava
          diante de um formulário de cadastro sem nenhuma saída para o app. A
          porta de entrada mais comum é o link do nosso próprio email, aberto
          dentro do navegador embutido do Gmail/Yahoo/Outlook: ele sempre
          aterrissa aqui, nunca no app.

          Medido em 30 dias: 208 contas caíram nesta página pelo menos uma vez,
          97 duas ou mais, e 18 caem em TODA abertura — essas nunca chegam ao
          app. A DeeAnn Buell escreveu 5 vezes em 8 horas e pediu reembolso de
          $74 USD achando que era ela que não sabia usar.

          Deliberadamente NÃO é um redirect: a avó logada na própria conta que
          veio criar a conta da filha seria expulsa da página que ela quer. A
          barra abre a porta sem fechar a outra; o formulário segue abaixo.
        */}
        {user && (
          <motion.div
            initial={{ y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5"
          >
            <div className="p-4 rounded-2xl bg-orange-500/[0.07] dark:bg-orange-500/10 ring-1 ring-orange-500/30 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 dark:text-gray-100 font-semibold text-[15px] leading-tight">
                  You are already signed in
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-[15px] truncate mt-0.5">
                  {user.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  trackCtaClick('welcome_hp_already_signed_in');
                  trackAuthStep('welcome_hp', 'already_signed_in');
                  navigate('/', { replace: true });
                }}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[0.98] transition-all text-white font-semibold text-[15px]"
              >
                Open the app
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Hero Header */}
        <motion.div
          initial={{ y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center pb-6"
        >
          {/* Success icon with orange glow */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12, stiffness: 150, delay: 0.1 }}
            className="relative mb-5"
          >
            <div
              className="absolute -inset-3 rounded-full opacity-40"
              style={{
                background: 'radial-gradient(circle, rgba(255,107,0,0.3) 0%, transparent 70%)',
                filter: 'blur(12px)',
              }}
            />
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-xl shadow-orange-500/20">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </motion.div>

          <motion.h1
            initial={{ y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-900 dark:text-foreground text-[26px] font-bold text-center leading-tight"
          >
            {mode === 'self'
              ? 'Your 3 Magic Words Are Ready'
              : 'Give the Gift of a Calmer Home'}
          </motion.h1>

          <motion.p
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-gray-600 dark:text-muted-foreground text-center mt-2 text-[15px] leading-relaxed max-w-[320px]"
          >
            {mode === 'self'
              ? 'Create your account to see your personalized phrases and talk to Sarah. Takes 30 seconds.'
              : "They'll get instant access to 200+ magic phrases and their own parenting coach."}
          </motion.p>
        </motion.div>

        {/* Mode Toggle */}
        <motion.div
          initial={{ y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34 }}
          className="mb-5"
        >
          <div className="flex bg-gray-100 dark:bg-white/5 rounded-2xl p-1 ring-1 ring-gray-200 dark:ring-white/10">
            <button
              type="button"
              onClick={() => { setMode('self'); setFormError(null); }}
              className={`flex-1 min-h-[48px] py-3 px-4 rounded-xl text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2 ${mode === 'self'
                  ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-foreground shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              <User className="w-4 h-4" />
              For Me

            </button>
            <button
              type="button"
              onClick={() => { setMode('gift'); setFormError(null); setAccountExists(false); }}
              className={`flex-1 min-h-[48px] py-3 px-4 rounded-xl text-[15px] font-medium transition-all duration-300 flex items-center justify-center gap-2 ${mode === 'gift'
                  ? 'bg-orange-50 text-orange-600 shadow-sm ring-1 ring-orange-200'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              <Gift className="w-4 h-4" />
              It's a Gift
            </button>
          </div>
        </motion.div>

        {/* What's Included */}
        <motion.div
          initial={{ y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34 }}
          className="mb-5"
        >
          <WhatsIncluded />
        </motion.div>

        {/* Gift Info Box — directs user to Profile page */}
        {mode === 'gift' && (
          <motion.div
            initial={{ y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-5 p-5 rounded-2xl bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-200 dark:ring-orange-500/20"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-accent" />
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-foreground">
                Want to share this as a gift?
              </p>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-[15px] leading-relaxed mb-3">
              Create your account first, then go to your <strong>Profile</strong> page where you can share access with up to 5 people. They'll get instant access, no extra cost.
            </p>
            <button
              type="button"
              onClick={() => { setMode('self'); setFormError(null); }}
              className="inline-flex items-center gap-1.5 text-orange-600 dark:text-orange-400 font-semibold text-[15px] hover:text-accent transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Create my account first
            </button>
          </motion.div>
        )}

        {/* Email Warning (self mode) */}
        {mode === 'self' && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-4"
          >
            <div className="p-3 rounded-xl bg-orange-500/5 dark:bg-orange-500/10 ring-1 ring-orange-500/20 flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 text-accent/70 flex-shrink-0" />
              <p className="text-orange-700/80 dark:text-orange-400/80 text-[15px]">
                Use the same email you used when purchasing
              </p>
            </div>
          </motion.div>
        )}

        {/* In-app browser warning (self mode) */}
        {mode === 'self' && <InAppBrowserWarning className="mb-4" />}

        {/* Account Already Exists */}
        <AnimatePresence>
          {accountExists && (
            <motion.div
              initial={{ y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              className="mb-5"
            >
              <div className="p-5 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="text-emerald-700 font-bold text-base">
                    You already have an account
                  </p>
                </div>

                <p className="text-emerald-600 text-[15px] mb-4">
                  Just sign in to access your program. Your content is waiting for you.
                </p>

                <Link
                  to="/auth"
                  className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-emerald-500 hover:bg-emerald-400 text-white text-center font-bold text-base rounded-xl transition-colors"
                >
                  Sign In Now
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <div className="mt-4 pt-4 border-t border-emerald-200">
                  <p className="text-emerald-700 dark:text-emerald-500 text-[15px] text-center">
                    Having trouble? Contact us at{' '}
                    <a href="mailto:sarah@codeofobedience.com" className="underline font-medium">
                      sarah@codeofobedience.com
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form Error */}
        <AnimatePresence>
          {formError && (
            <motion.div
              initial={{ y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              className="mb-4"
            >
              <div className="p-4 rounded-xl bg-red-50 ring-1 ring-red-200">
                <p className="text-red-600 text-[15px] text-center">
                  {formError}
                </p>
                <p className="text-red-700 dark:text-red-400 text-[15px] text-center mt-2">
                  Need help?{' '}
                  <a href="mailto:sarah@codeofobedience.com" className="underline font-medium">
                    Contact support
                  </a>
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Self Mode Form */}
        {mode === 'self' && !accountExists && (
          <motion.form
            onSubmit={handleSubmit}
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="space-y-4 pb-4"
          >
            <PremiumInput
              type="email"
              value={email}
              onChange={(v) => { markStarted(); setEmail(v); }}
              placeholder="Enter your email"
              label="Email"
              disabled={loading}
              error={email.length > 3 ? emailError : undefined}
              success={emailValid}
            />

            {emailSuggestion && (
              <button
                type="button"
                onClick={() => setEmail(emailSuggestion)}
                className="-mt-1 flex w-full items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-50 px-4 py-3 text-left transition-colors active:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:active:bg-amber-500/20"
              >
                <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="text-[15px] leading-snug text-gray-800 dark:text-white/85">
                  Did you mean <span className="font-bold text-amber-700 dark:text-amber-300">{emailSuggestion}</span>?
                  {' '}Tap to use it.
                </span>
              </button>
            )}

            <div>
              <PremiumInput
                type="password"
                value={password}
                onChange={(v) => { markStarted(); setPassword(v); }}
                placeholder="Create a password (6+ characters)"
                label="Password"
                disabled={loading}
                success={passwordValid}
              />
              <PasswordStrength password={password} />
              {password.length > 0 && password.length < 6 && (
                <p className="mt-1.5 text-[15px] text-red-700 dark:text-red-400">Password must be at least 6 characters</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-3">
              <motion.button
                type="submit"
                disabled={!formValid || loading}
                whileTap={{ scale: 0.98 }}
                className={`
                  w-full h-14 rounded-2xl font-semibold text-base
                  transition-all duration-300 flex items-center justify-center gap-2
                  ${!formValid || loading
                    ? 'bg-gray-100 dark:bg-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed ring-1 ring-gray-200 dark:ring-white/5'
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-400 hover:to-orange-500 shadow-xl shadow-orange-500/20 dark:shadow-orange-500/10'
                  }
                `}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    Create My Account
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </div>
          </motion.form>
        )}

        {/* Gift mode — no form needed, info box above handles it */}

        {/* Sign In link (self mode) */}
        {mode === 'self' && !accountExists && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-5 text-center"
          >
            <p className="text-gray-500 dark:text-gray-400 text-[15px]">
              Already have an account?{' '}
              <Link
                to="/auth"
                className="text-accent font-medium hover:brightness-110 transition-colors"
              >
                Sign In
              </Link>
            </p>
          </motion.div>
        )}

        {/* Spacer */}
        <div className="flex-1 min-h-8" />

        {/* Support Section */}
        <motion.div
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
            <span className="text-xs text-gray-400 uppercase tracking-widest font-medium">Need help?</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={SUPPORT.getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-200 dark:ring-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-500/15 transition-colors min-h-[48px]"
            >
              <MessageCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-emerald-600 dark:text-emerald-400 text-[15px] font-medium">WhatsApp</span>
            </a>

            <a
              href="mailto:sarah@codeofobedience.com"
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-200 dark:ring-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/15 transition-colors min-h-[48px]"
            >
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-blue-600 dark:text-blue-400 text-[15px] font-medium">Email</span>
            </a>
          </div>

          <p className="text-[15px] text-gray-500 dark:text-gray-400 text-center mt-6 leading-relaxed">
            Your purchase will appear on your bank statement as a charge from <strong className="text-gray-700 dark:text-gray-300 font-semibold">Hotmart</strong>, our trusted payment partner.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
